import { useEffect, useRef, type ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Activity,
    ArrowRight,
    BadgeCheck,
    Boxes,
    Code,
    Database,
    FileBox,
    FlaskConical,
    FolderOpen,
    Layers,
    Quote,
    Ship,
    Upload,
} from "lucide-react";

import SearchInputBar from "./search/SearchInputBar";
import { SearchActions, SettingsActions } from "./sagas/actions";
import { ICategory } from "./interfaces/category";
import { Skeleton } from "./components/ui/skeleton";
import hero from "./assets/pexels-tristan-youel-211956291-11776273.jpg";

/** Presentation only: an icon and a one-line description per record type. */
const CATEGORY_META: Record<string, { icon: ReactNode; blurb: string }> = {
    cruises: { icon: <Ship />, blurb: "Expeditions, stations and shipboard logs." },
    digital_objects: { icon: <FileBox />, blurb: "Measurement files, scans and derived products." },
    experiments: { icon: <FlaskConical />, blurb: "Mesocosm and laboratory experiment records." },
    analytical_runs: { icon: <Activity />, blurb: "Instrument runs with calibration metadata." },
    digital_object_identifiers: { icon: <BadgeCheck />, blurb: "Published, citable datasets with a DOI." },
    projects: { icon: <FolderOpen />, blurb: "Funded research programmes and consortia." },
    models: { icon: <Boxes />, blurb: "Model configurations and output archives." },
    awesome_list: { icon: <Code />, blurb: "Research software and analysis notebooks." },
};

const SUGGESTIONS = ["North Sea nutrients", "RV Pelagia", "Wadden Sea benthos", "sediment core"];

const Home = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const searchInputRef = useRef<HTMLInputElement>(null);
    const categories: ICategory[] = useSelector((state: any) => state.settings.categories) ?? [];

    useEffect(() => {
        dispatch({ type: SettingsActions.FETCH_ATTRIBUTES });
        dispatch({ type: SettingsActions.FETCH_CATEGORIES });
    }, [dispatch]);

    const handleSuggestion = (term: string) => {
        dispatch({ type: SearchActions.SET_SEARCH_TERM, payload: term });
        dispatch({ type: SearchActions.SET_SEARCH_RELATED, payload: false });
        dispatch({ type: SearchActions.CLEAR_SELECTED });
        dispatch({ type: SearchActions.INITIAL_SEARCH });
        navigate('/results');
    };

    const handleCategory = (indexName: string) => {
        navigate('/results/category/' + indexName);
    };

    return (
        <div className="w-full">

            {/* ─────────────────────────── hero ─────────────────────────── */}
            <section className="relative overflow-hidden border-b border-line">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${hero})` }}
                    aria-hidden
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            'linear-gradient(160deg, rgba(0,20,72,.92) 0%, rgba(0,25,90,.72) 45%, rgba(0,50,180,.56) 100%)',
                    }}
                    aria-hidden
                />

                <div className="relative mx-auto w-full max-w-[1680px] px-4 py-14 sm:px-6 lg:py-[76px]">
                    <div className="max-w-[720px]">
                        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/[.14] px-3 py-1.5 text-xs font-medium text-[#e5eaf7]">
                            <Database className="h-3.5 w-3.5" />
                            FAIR marine research data · NIOZ &amp; Utrecht University
                        </div>

                        <h1 className="m-0 mb-4 text-[32px] font-semibold leading-[1.08] tracking-[-.025em] text-white sm:text-[40px] lg:text-[46px]">
                            Find, inspect and download Dutch marine research data.
                        </h1>

                        <p className="m-0 mb-8 max-w-[560px] text-[15px] leading-relaxed text-[#c2cdea] sm:text-[17px]">
                            Oceanographic measurements, cruise records, sediment cores and published
                            datasets from NIOZ, Utrecht University and partner institutes — in one search.
                        </p>

                        <div className="max-w-[640px]">
                            <SearchInputBar searchInputRef={searchInputRef} hero minimized />
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <span className="mr-1 text-xs text-[#b2c1e7]">Try</span>
                            {SUGGESTIONS.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => handleSuggestion(s)}
                                    className="rounded-full border border-white/20 bg-white/[.12] px-3 py-1.5 text-xs text-[#e5eaf7] transition-colors hover:bg-white/[.22]"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────────────── browse by category ─────────────────── */}
            <section className="mx-auto w-full max-w-[1680px] px-4 pb-4 pt-9 sm:px-6">
                <div className="mb-3.5 flex items-baseline justify-between gap-4">
                    <h2 className="m-0 text-[15px] font-semibold text-ink">Browse by data category</h2>
                    {categories.length > 0 && (
                        <span className="text-xs text-ink-3">{categories.length} categories</span>
                    )}
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {categories.length === 0
                        ? Array.from({ length: 8 }).map((_, i) => (
                            <Skeleton key={i} className="h-[104px] rounded-lg" />
                        ))
                        : categories.map((cat) => {
                            const meta = CATEGORY_META[cat.indexName];
                            return (
                                <button
                                    key={cat.indexName}
                                    type="button"
                                    onClick={() => handleCategory(cat.indexName)}
                                    className="nz-card nz-card-hover group flex flex-col gap-2.5 p-4 text-left hover:-translate-y-0.5"
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-soft text-brand [&_svg]:h-4 [&_svg]:w-4">
                                        {meta?.icon ?? <Layers />}
                                    </span>
                                    <span className="text-sm font-semibold text-ink">
                                        {cat.label || cat.name || cat.indexName}
                                    </span>
                                    <span className="text-xs leading-relaxed text-ink-3">
                                        {meta?.blurb ?? 'Records in this category.'}
                                    </span>
                                </button>
                            );
                        })}
                </div>
            </section>

            {/* ───────────────────────── info panels ────────────────────── */}
            <section className="mx-auto grid w-full max-w-[1680px] gap-4 px-4 pb-16 pt-4 sm:px-6 lg:grid-cols-3">
                <div className="nz-card p-[18px] lg:col-span-2">
                    <h2 className="m-0 mb-3.5 text-sm font-semibold text-ink">About this portal</h2>
                    <p className="m-0 mb-4 max-w-[70ch] text-[13.5px] leading-relaxed text-ink-2">
                        The Dutch Marine Science Portal brings oceanographic measurements, cruise
                        records, experiments, analytical runs and published datasets together behind
                        one search. Every record carries structured metadata, so you can filter by
                        category, creator, geography and time before you download anything.
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                        {[
                            ['Findable', 'Full-text search across all catalogue metadata.'],
                            ['Accessible', 'Clear access levels, with a basket for bulk downloads.'],
                            ['Interoperable', 'Standard formats, vocabularies and an open API.'],
                            ['Reusable', 'Documented provenance, licences and minted DOIs.'],
                        ].map(([title, body]) => (
                            <div key={title} className="rounded-md border border-line-2 bg-surface-2 p-3">
                                <div className="mb-1 text-[13px] font-semibold text-ink">{title}</div>
                                <div className="text-xs leading-relaxed text-ink-3">{body}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col gap-4">
                    <div className="rounded-lg border border-brand-line bg-brand-soft p-[18px]">
                        <div className="mb-2 flex items-center gap-2 text-brand">
                            <Upload className="h-[15px] w-[15px]" />
                            <h2 className="m-0 text-sm font-semibold text-ink">Deposit your data</h2>
                        </div>
                        <p className="m-0 mb-3 text-[13px] leading-relaxed text-ink-2">
                            Publish with a DOI, long-term preservation and FAIR metadata review by our
                            data stewards.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/page/share-data')}
                            className="nz-btn nz-btn-warm h-8 rounded-lg text-[13px]"
                        >
                            How to submit <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    </div>

                    <div className="nz-card p-[18px]">
                        <div className="mb-2 flex items-center gap-2 text-ink-2">
                            <Quote className="h-[15px] w-[15px]" />
                            <h2 className="m-0 text-sm font-semibold text-ink">Citing our data</h2>
                        </div>
                        <p className="m-0 mb-3 text-[13px] leading-relaxed text-ink-2">
                            Every published record has a citation snippet and a persistent identifier.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/page/cite-data')}
                            className="nz-btn h-8 rounded-lg text-[13px]"
                        >
                            Citation guidelines <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
